import {FetchCAPIError} from '@github-ui/copilot-shared-ui/capi/fetch-capi'
import {sendEvent} from '@github-ui/hydro-analytics'

import {
  SettingsConflictError,
  SettingsUnavailableError,
  UnsafeSettingsError,
  UnsupportedSettingsSchemaError,
} from '../data/mcp-settings-api'
import type {McpTransport} from '../types/mcp'

type McpAction = 'create' | 'edit' | 'remove'

function transportMetadata(transport: McpTransport | undefined) {
  switch (transport) {
    case 'http':
    case 'sse':
      return transport
    case 'local':
    case 'stdio':
      return 'local'
    default:
      return 'unknown'
  }
}

function errorCategory(error: unknown) {
  if (error instanceof SettingsConflictError) return 'settings_conflict'
  if (error instanceof SettingsUnavailableError) return 'settings_unavailable'
  if (error instanceof UnsafeSettingsError) return 'unsafe_settings'
  if (error instanceof UnsupportedSettingsSchemaError) return 'unsupported_schema'
  if (error instanceof FetchCAPIError) return error.hasResponse ? 'http' : 'network'
  return 'unexpected'
}

export function sendMcpUiEvent(
  event: 'add_click' | 'add_server_click' | 'edit_click' | 'remove_click' | 'cancel' | 'validation_failed',
  context: {
    source: 'header' | 'add_menu' | 'row' | 'dialog'
    action?: McpAction
    transport?: McpTransport
  },
) {
  sendEvent(`customize_mcp.${event}`, {
    source: context.source,
    ...(context.action === undefined ? {} : {action: context.action}),
    ...(context.transport === undefined ? {} : {transport: transportMetadata(context.transport)}),
  })
}

export function startMcpAttempt(action: McpAction, transport: McpTransport | undefined, renamed = false) {
  const operation = action === 'remove' ? 'remove' : 'save'
  const context = {
    source: 'dialog',
    action,
    transport: transportMetadata(transport),
    ...(action === 'edit' ? {renamed: renamed === true} : {}),
    attempt_id: crypto.randomUUID(),
  }
  const startedAt = performance.now()
  let finished = false

  sendEvent(`customize_mcp.${operation}_attempt`, context)

  return {
    finish(outcome: 'success' | 'failure', error?: unknown) {
      // A cache update or refresh can fail after persistence has succeeded.
      if (finished) return
      finished = true
      sendEvent(`customize_mcp.${operation}_result`, {
        ...context,
        outcome,
        ...(outcome === 'failure' ? {error_category: errorCategory(error)} : {}),
        duration_ms: Math.round(performance.now() - startedAt),
      })
    },
  }
}
